import { useCallback } from 'react';

/**
 * Prevents a Headless UI `<Menu>`/`<Dialog>` from closing when the user presses
 * or drags a scrollbar inside the popup.
 *
 * Headless UI's `useOutsideClick` closes the popup on any `pointerup` whose
 * originating `pointerdown` it resolves to a target outside the panel. On
 * Windows/Edge, pressing an element's native scrollbar produces pointer events
 * that Headless treats as an outside click, so "grabbing the slider" collapses
 * the popup (reported in the speaker picker).
 *
 * We register a single, app-wide capture-phase listener pair that detects when a
 * press lands in the scrollbar gutter of any guarded element and neutralises the
 * following `pointerup` via `preventDefault()`. Headless's `useOutsideClick`
 * bails out early when `event.defaultPrevented` is true, so the popup stays open.
 *
 * Ordering matters: our listeners must fire before Headless's. Headless attaches
 * its document listener from a passive `useEffect`, while this guard arms itself
 * from a ref callback (commit phase, before passive effects), so our capture
 * listeners are always registered — and therefore dispatched — first.
 */

const guardedTargets = new Set<HTMLElement>();
let pressedOnScrollbar = false;
let armed = false;

const pointInScrollbar = (el: HTMLElement, x: number, y: number): boolean => {
  const rect = el.getBoundingClientRect();
  if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
    return false;
  }
  // Gutter thickness = border-box size minus the padding-box (content) size.
  const verticalBar = el.offsetWidth - el.clientWidth;
  const horizontalBar = el.offsetHeight - el.clientHeight;
  const inVertical = verticalBar > 0 && x >= rect.right - verticalBar;
  const inHorizontal = horizontalBar > 0 && y >= rect.bottom - horizontalBar;
  return inVertical || inHorizontal;
};

const onPointerDown = (e: PointerEvent) => {
  pressedOnScrollbar = false;
  for (const el of Array.from(guardedTargets)) {
    // Elements are re-created every time the popup opens; prune stale ones.
    if (!el.isConnected) {
      guardedTargets.delete(el);
      continue;
    }
    if (pointInScrollbar(el, e.clientX, e.clientY)) {
      pressedOnScrollbar = true;
      break;
    }
  }
};

const onPointerUp = (e: PointerEvent) => {
  if (pressedOnScrollbar) {
    // Makes Headless UI's useOutsideClick treat this as handled, not an
    // outside click.
    e.preventDefault();
    pressedOnScrollbar = false;
  }
};

const arm = () => {
  if (armed || typeof document === 'undefined') return;
  armed = true;
  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('pointerup', onPointerUp, true);
};

/**
 * Returns a callback ref to attach to a scrollable element inside a Headless UI
 * popup. Pressing that element's scrollbar will no longer close the popup.
 */
export function useScrollbarMenuGuard() {
  return useCallback((el: HTMLElement | null) => {
    arm();
    if (el) guardedTargets.add(el);
  }, []);
}
